import { inject, Injectable } from '@angular/core';
import { PermissionsService } from '../permissionsService/permissions.service';
import { AccountService } from '../account/account.service';
import { AccountCategory } from 'src/app/models/api/accountManagement.model';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { UserService } from '../user/user.service';
import { FeatureFlagsService } from '../featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';

@Injectable({
  providedIn: 'root',
})
export class AccessService {
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _accountService = inject(AccountService);
  private readonly _userService = inject(UserService);
  private readonly _featureFlagsService = inject(FeatureFlagsService);

  public renderExportCSVButton(accountCategory: AccountCategory | null): boolean {
    return (
      this._permissionsService.userHasPermission(PERMISSIONS.GLOBAL_VIEWER) &&
      this._accountService.accountHasFullAccess(accountCategory)
    );
  }

  public renderAPITabAndDocsButton(accountCategory: AccountCategory | null): boolean {
    return this._accountService.accountHasFullAccess(accountCategory);
  }

  /**
   * Check if user has access to AIDR (AI Detection & Response) features.
   * User has AIDR access if they have any aidr_* permission.
   */
  public hasAidrAccess(): boolean {
    return this._hasAccessByPermissionPrefix('aidr_');
  }

  /**
   * Check if user has access to AISPM (AI Security Posture Management) features.
   * User has AISPM access if they have any spm_* permission.
   */
  public hasAispmAccess(): boolean {
    return this._hasAccessByPermissionPrefix('spm_');
  }

  /**
   * Check if user has access based on permission prefix.
   * Returns true if user has any permission starting with the given prefix.
   * Note: global_* permissions do NOT grant cross-product access.
   */
  private _hasAccessByPermissionPrefix(permissionPrefix: string): boolean {
    if (this._permissionsService.isAdmin()) {
      return true;
    }

    // If RBAC is not enabled, all users have access (legacy behavior)
    if (!this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.rbacAccessManagement)) {
      return true;
    }

    const accessPermissions = this._userService.accessPermissions;

    // If no access permissions structure, fall back to legacy (allow access)
    if (Object.keys(accessPermissions).length === 0) {
      return true;
    }

    // Check if user has any permission with the given prefix
    return Object.keys(accessPermissions).some((key) => key.startsWith(permissionPrefix));
  }
}
